/**
 * The main drill-down menu, behind the burger button. It renders the locked menu
 * tree (design/chartplotter-menu-structure.md) as a data-driven drill-down with
 * breadcrumb navigation, a favourites row of quick chips at the top, and live
 * search that flattens the whole tree and shows each hit with its breadcrumb
 * trail. Everything advanced lives here so the chart stays uncluttered; the
 * common things are one or two taps in, the rare things are findable by search.
 *
 * Selecting a leaf dispatches through the pure resolver (menuActions.ts) to the
 * real stores: open a sheet, set the palette, arm a tool, run a command, or show
 * an honest "coming soon" for planned features. Wiring a feature is a one-line
 * change in the tree, not here.
 */
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useShellStore, type SheetName } from '@/store/shell';
import { useChartPlotterStore, type ActiveTool } from '@/store';
import {
  MENU_TREE,
  searchMenu,
  findMenuLeaf,
  type MenuGroup,
  type MenuLeaf,
  type FlatMenuHit,
} from '@/ui/menu/menuTree';
import { useMenuFavouritesStore, mostUsedFrom, DEFAULT_FAV_LEAF } from '@/store/menuFavourites';
import {
  resolveMenuAction,
  commandOpensSheet,
  type ResolvedMenuAction,
} from '@/ui/menu/menuActions';
import { usePassagePlanStore } from '@/store/passagePlan';
import { useWeatherOverlayStore } from '@/store/weatherOverlay';
import { useRoutePlanStore } from '@/store/routePlan';
import { dropMobAtVessel } from './mobActions';
import { haptic } from '@/ui/haptics';
import { playMobConfirmation } from '@/ui/alarmSound';
import { ChevronIcon } from './icons';
import { MenuControl } from './MenuControls';
import { useBackHandler } from './useBackHandler';

/** Where we are in the drill-down: the top groups, a group/leaf, or a focused control. */
type Level =
  | { kind: 'root' }
  | { kind: 'group'; group: MenuGroup }
  | { kind: 'leaf'; group: MenuGroup; leaf: MenuLeaf }
  | { kind: 'control'; group: MenuGroup; leaf: MenuLeaf; control: string };

/**
 * Bottom-sheet detents, as fractions of the viewport height (dvh) so the sheet
 * adapts to phone, tablet, and landscape. The menu opens at HALF; flicking up
 * snaps to FULL, dragging the header below DISMISS_BELOW closes it. MIN_DVH just
 * stops a live drag collapsing to nothing before the dismiss decision is made.
 */
const HALF_DVH = 54;
const FULL_DVH = 92;
const DISMISS_BELOW_DVH = 34;
const MIN_DVH = 8;

export function MenuSheet() {
  const activeSheet = useShellStore((s) => s.activeSheet);
  const open = activeSheet === 'menu';
  const openSheet = useShellStore((s) => s.openSheet);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const setMenuLocation = useShellStore((s) => s.setMenuLocation);
  const setPalette = useChartPlotterStore((s) => s.setPalette);
  const setActiveTool = useChartPlotterStore((s) => s.setActiveTool);
  const recordUse = useMenuFavouritesStore((s) => s.recordUse);
  const favIds = useMenuFavouritesStore((s) => s.favIds);
  const usage = useMenuFavouritesStore((s) => s.usage);
  // The single most-used feature that isn't already a favourite, pinned at the
  // far left of the bar; recomputed as usage changes so the one tool we reach for
  // most is always the first tap even if we never starred it. We exclude both the
  // favourite ids and the leaf a default already covers (e.g. the Route default
  // and the Routes leaf), so the auto slot never doubles up a built-in.
  // Derived in a memo, NOT inside the selector: a selector that builds a fresh
  // array every call gives useSyncExternalStore an unstable snapshot, which
  // React throws on ("getSnapshot should be cached"), and MenuSheet renders on
  // every boot, so that throw took the whole app down. favIds + usage are stable
  // store slices, so this only recomputes when they actually change.
  const autoIds = useMemo(() => {
    const exclude = [
      ...favIds,
      ...favIds.map((id) => DEFAULT_FAV_LEAF[id]).filter((x): x is string => !!x),
    ];
    return mostUsedFrom(usage, 1, exclude);
  }, [usage, favIds]);

  const [level, setLevel] = useState<Level>({ kind: 'root' });
  const [query, setQuery] = useState('');

  // --- Draggable bottom sheet ---
  const [heightDvh, setHeightDvh] = useState(HALF_DVH);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ startY: number; curH: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // On open: reset height and depth, then land on a target group (the layers
  // button asks for Layers & Chart via openMenuAt) or the top level (the burger).
  // The target is read synchronously so clearing it does not re-fire this effect.
  useEffect(() => {
    if (!open) return;
    setHeightDvh(HALF_DVH);
    setQuery('');
    const pending = useShellStore.getState().pendingMenuTarget;
    if (pending) {
      const g = MENU_TREE.groups.find((x) => x.id === pending.group);
      const leaf = g && pending.leaf ? g.items.find((l) => l.id === pending.leaf) : undefined;
      const a = leaf ? resolveMenuAction(leaf) : null;
      if (g && leaf && a && a.kind === 'control') {
        setLevel({ kind: 'control', group: g, leaf, control: a.control });
      } else if (g) {
        setLevel({ kind: 'group', group: g });
      } else {
        setLevel({ kind: 'root' });
      }
      useShellStore.getState().setPendingMenuTarget(null);
    } else {
      setLevel({ kind: 'root' });
    }
  }, [open]);

  // Publish the current drill location (group / leaf / control) for the feedback
  // self-test tool, so a note records exactly which menu screen was open.
  useEffect(() => {
    if (!open) {
      setMenuLocation(null);
      return;
    }
    const loc =
      level.kind === 'root'
        ? 'Menu (top)'
        : level.kind === 'group'
          ? level.group.label
          : `${level.group.label} → ${level.leaf.label}`;
    setMenuLocation(loc);
  }, [open, level, setMenuLocation]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [level, query]);

  function onDragStart(e: ReactPointerEvent<HTMLDivElement>) {
    // Let taps on the back/close buttons through; only blank header drags resize.
    if ((e.target as HTMLElement).closest('button')) return;
    drag.current = { startY: e.clientY, curH: heightDvh };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  }
  function onDragMove(e: ReactPointerEvent<HTMLDivElement>) {
    const d = drag.current;
    if (!d) return;
    // Drag up (negative delta) grows the sheet; convert px to dvh live.
    const deltaVh = ((e.clientY - d.startY) / window.innerHeight) * 100;
    const next = Math.min(FULL_DVH, Math.max(MIN_DVH, d.curH - deltaVh));
    d.curH = next;
    setHeightDvh(next);
  }
  function onDragEnd() {
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    if (!d) return;
    // Flicked/dragged down past the threshold: dismiss. Otherwise snap to the
    // nearer of the two detents.
    if (d.curH < DISMISS_BELOW_DVH) {
      resetAndClose();
      return;
    }
    setHeightDvh(d.curH >= (HALF_DVH + FULL_DVH) / 2 ? FULL_DVH : HALF_DVH);
  }

  // Reset the menu's own state (depth, query, height) to the top, so it never
  // lands deep when reopened. Does NOT touch the active sheet, so a command that
  // hands over to another sheet can tidy the menu without closing that sheet.
  function resetMenu() {
    setLevel({ kind: 'root' });
    setQuery('');
    setHeightDvh(HALF_DVH);
  }

  // Reset and close the menu entirely.
  function resetAndClose() {
    resetMenu();
    closeSheet();
  }

  // Unwind the menu one step per hardware back press: clear an active search
  // first, then drill up a level (control/leaf -> group -> root), and only close
  // the whole sheet from the root. Returns true whenever the menu is open, so the
  // Android back button never falls through to exit while we're in the menu.
  function menuBack(): boolean {
    if (query.trim()) {
      setQuery('');
      return true;
    }
    if (level.kind === 'leaf' || level.kind === 'control') {
      setLevel({ kind: 'group', group: level.group });
      return true;
    }
    if (level.kind === 'group') {
      setLevel({ kind: 'root' });
      return true;
    }
    resetAndClose();
    return true;
  }
  // Priority 20: above the generic sheet-close handler so the menu unwinds its
  // own drill levels before anything closes the sheet outright.
  useBackHandler(() => (open ? menuBack() : false), 20);

  /**
   * A favourite chip either opens a sheet directly, or (action "menu:group/leaf")
   * navigates within the already-open menu to a focused control. The menu-target
   * form is used where a favourite maps to an in-menu screen (e.g. Anchor watch),
   * which has no standalone sheet to open.
   */
  function handleFav(action?: string) {
    if (!action) return;
    if (action.startsWith('menu:')) {
      const [groupId, leafId] = action.slice(5).split('/');
      const group = MENU_TREE.groups.find((x) => x.id === groupId);
      const leaf = group && leafId ? group.items.find((l) => l.id === leafId) : undefined;
      const a = leaf ? resolveMenuAction(leaf) : null;
      setQuery('');
      if (group && leaf && a && a.kind === 'control') {
        setLevel({ kind: 'control', group, leaf, control: a.control });
      } else if (group && leaf) {
        onLeaf(group, leaf);
      } else if (group) {
        setLevel({ kind: 'group', group });
      }
      return;
    }
    openSheet(action as Parameters<typeof openSheet>[0]);
    setLevel({ kind: 'root' });
  }

  /** Carry out a resolved action against the real stores. */
  function dispatch(leaf: MenuLeaf, a: ResolvedMenuAction) {
    switch (a.kind) {
      case 'sheet':
        openSheet(a.sheet);
        break;
      case 'palette':
        setPalette(a.palette);
        showToast(`${leaf.label} palette`);
        resetAndClose();
        break;
      case 'tool':
        setActiveTool(a.tool as Parameters<typeof setActiveTool>[0]);
        showToast(`${leaf.label} armed`);
        resetAndClose();
        break;
      case 'command': {
        const opened = runCommand(a, { openSheet, setActiveTool, showToast });
        // 'provider' leaves the menu open so the skipper can keep picking; a
        // command that opened a sheet has already replaced the menu, so only tidy
        // the menu's internals rather than closing the sheet back over itself;
        // everything else closes the menu.
        if (a.command === 'provider') break;
        if (opened) resetMenu();
        else resetAndClose();
        break;
      }
      case 'planned':
        showToast(`${leaf.label} — coming soon`);
        break;
      default:
        showToast(`${leaf.label} — not yet wired`);
    }
  }

  function onLeaf(group: MenuGroup, leaf: MenuLeaf) {
    haptic('select');
    const a = resolveMenuAction(leaf);
    // Count opening a real feature (not just drilling into a group) so the auto
    // most-used slots reflect what we actually use.
    if (a.kind !== 'parent') recordUse(leaf.id);
    if (a.kind === 'parent') {
      // Clearing the query matters when this leaf was picked from a search hit:
      // the view is gated on `query`, so without this the drill never shows.
      setQuery('');
      setLevel({ kind: 'leaf', group, leaf });
      return;
    }
    // A focused control opens INSIDE the menu (breadcrumb back returns here),
    // instead of opening the whole Layers sheet.
    if (a.kind === 'control') {
      setQuery('');
      setLevel({ kind: 'control', group, leaf, control: a.control });
      return;
    }
    // A sheet replaces the menu; reset our depth so reopening starts at root.
    if (a.kind === 'sheet') {
      setLevel({ kind: 'root' });
      setQuery('');
    }
    dispatch(leaf, a);
  }

  // The quick favourites bar reads left to right: the single auto most-used slot
  // first, then the manual favourites strip. Each id is resolved to a chip up
  // front: a built-in default (a `fav-*` id in MENU_TREE.favourites) keeps its
  // original icon and label and launches via its action, so Tidal, which has no
  // menu leaf, still renders; anything else resolves through findMenuLeaf. Ids
  // that resolve to neither (e.g. a feature removed in a later build) are quietly
  // dropped. Resolved in memos over the stable favIds/autoIds slices so the bar
  // hands useSyncExternalStore a cached snapshot rather than a fresh array.
  const manualFavs = useMemo(() => favIds.map(resolveFavChip).filter((x): x is FavChip => !!x), [favIds]);
  const autoFavs = useMemo(() => autoIds.map(resolveFavChip).filter((x): x is FavChip => !!x), [autoIds]);
  function runFavChip(chip: FavChip) {
    if (chip.action != null) handleFav(chip.action);
    else launchFav(chip.id);
  }
  function launchFav(id: string) {
    const loc = findMenuLeaf(id);
    if (loc) onLeaf(loc.group, loc.leaf);
  }

  const trail: string[] =
    level.kind === 'group'
      ? [level.group.label]
      : level.kind === 'leaf' || level.kind === 'control'
        ? [level.group.label, level.leaf.label]
        : [];

  return (
    <div
      className={`acp-sheet acp-menu${open ? ' show' : ''}${dragging ? ' dragging' : ''}`}
      style={{ height: open ? `${heightDvh}dvh` : undefined }}
      role="dialog"
      aria-modal="true"
      aria-hidden={!open}
      aria-label="Menu"
    >
      {/* Drag zone: the grab handle and breadcrumb bar resize the sheet. */}
      <div
        className="acp-menu-drag"
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
      >
        <div className="acp-grab" />

        {/* Breadcrumb / back */}
        <div className="acp-menu-bar">
          {level.kind !== 'root' ? (
            <button
              type="button"
              className="acp-menu-back"
              onClick={() => menuBack()}
              aria-label="Back"
            >
              <ChevronIcon dir="left" /> {trail[trail.length - 2] ?? 'Menu'}
            </button>
          ) : (
            <h2 className="acp-menu-title">Menu</h2>
          )}
          <button
            type="button"
            className="acp-menu-close"
            onClick={resetAndClose}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Search + favourites head. It's the first block inside the scrolling list
          now, so it scrolls away with the rest of the menu rather than staying
          pinned: a scroll moves the whole menu, search bar and favourites too. */}
      <div className="acp-menu-scroll" ref={scrollRef}>
        <div className="acp-menu-head">
        <input
          className="acp-menu-search"
          type="search"
          inputMode="search"
          placeholder="Search every feature…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search the menu"
        />
        {level.kind === 'root' && !query.trim() && (
          <>
            <div className="acp-menu-section">Favourites</div>
            <div className="acp-menu-favs">
              {/* The single auto slot at the far left: the most-used feature not
                  already a favourite, marked so it reads as automatic rather than
                  something we chose. */}
              {autoFavs.map((chip) => (
                <button
                  key={`auto-${chip.id}`}
                  type="button"
                  className="acp-fav-chip auto"
                  onClick={() => runFavChip(chip)}
                  title="Most used — updates automatically"
                >
                  <span className="f-badge" aria-hidden="true">
                    auto
                  </span>
                  <span className="f-ic" aria-hidden="true">
                    {chip.icon}
                  </span>
                  <span className="f-tx">{chip.label}</span>
                </button>
              ))}
              {/* The manual favourites strip to its right: the built-in defaults
                  out of the box, plus anything added by long-pressing a menu item.
                  Fixed width, so a new favourite pushes the right-most one off. */}
              {manualFavs.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  className="acp-fav-chip"
                  onClick={() => runFavChip(chip)}
                >
                  <span className="f-ic" aria-hidden="true">
                    {chip.icon}
                  </span>
                  <span className="f-tx">{chip.label}</span>
                </button>
              ))}
            </div>
          </>
        )}
        </div>
        {query.trim() ? (
          <SearchResults query={query} onPick={(h) => onLeaf(findGroup(h), h.leaf)} />
        ) : level.kind === 'root' ? (
          <RootView onGroup={(g) => setLevel({ kind: 'group', group: g })} />
        ) : level.kind === 'group' ? (
          <ItemList
            items={level.group.items}
            onPick={(leaf) => onLeaf(level.group, leaf)}
            note={level.group.note}
          />
        ) : level.kind === 'control' ? (
          <MenuControl
            control={level.control}
            onNavigate={(t) => {
              // A long-press on an overlay row asks to jump to that overlay's
              // own settings screen. Resolve the leaf and reuse onLeaf so it
              // opens exactly as tapping that menu item would (control or sheet).
              const g = MENU_TREE.groups.find((x) => x.id === t.group);
              const l = g?.items.find((i) => i.id === t.leaf);
              if (g && l) onLeaf(g, l);
            }}
          />
        ) : (
          <ItemList
            items={level.leaf.children ?? []}
            onPick={(leaf) => onLeaf(level.group, leaf)}
          />
        )}
      </div>
    </div>
  );
}

/** A resolved favourites-bar chip: a built-in default (launched by its action)
 *  or a menu leaf (launched by its id). */
interface FavChip {
  id: string;
  icon?: string;
  label: string;
  /** Set only for a built-in default; launched through handleFav rather than as a leaf. */
  action?: string;
}

/**
 * Resolve a favourite id to a renderable chip. A built-in default (a `fav-*` id
 * in MENU_TREE.favourites) keeps its original icon, label, and action, so the
 * Tidal default, which opens the Tides sheet directly and has no menu leaf, still
 * renders. Anything else resolves through the menu tree. Ids that resolve to
 * neither return null and are dropped from the bar.
 */
function resolveFavChip(id: string): FavChip | null {
  const def = MENU_TREE.favourites.find((f) => f.id === id);
  if (def) return { id: def.id, icon: def.icon, label: def.label, action: def.action };
  const loc = findMenuLeaf(id);
  if (loc) return { id: loc.leaf.id, icon: loc.leaf.icon, label: loc.leaf.label };
  return null;
}

/** Find which group a flattened hit belongs to (the first trail label). */
function findGroup(hit: FlatMenuHit): MenuGroup {
  const top = hit.trail[0];
  return MENU_TREE.groups.find((g) => g.label === top) ?? MENU_TREE.groups[0];
}

function RootView({ onGroup }: { onGroup: (g: MenuGroup) => void }) {
  return (
    <>
      <div className="acp-menu-groups">
        {MENU_TREE.groups.map((g) => (
          <button key={g.id} type="button" className="acp-menu-row" onClick={() => onGroup(g)}>
            {g.icon && (
              <span className="ic" aria-hidden="true">
                {g.icon}
              </span>
            )}
            <span className="lbl">
              {g.label}
              {g.subtitle && <span className="sub">{g.subtitle}</span>}
            </span>
            <ChevronIcon dir="right" />
          </button>
        ))}
      </div>
    </>
  );
}

function ItemList({
  items,
  onPick,
  note,
}: {
  items: MenuLeaf[];
  onPick: (leaf: MenuLeaf) => void;
  note?: string;
}) {
  const favIds = useMenuFavouritesStore((s) => s.favIds);
  const toggleFavourite = useMenuFavouritesStore((s) => s.toggleFavourite);
  const addFavourite = useMenuFavouritesStore((s) => s.addFavourite);
  const showToast = useShellStore((s) => s.showToast);
  return (
    <>
      {note && <p className="acp-menu-note">{note}</p>}
      {items.map((leaf) => {
        const a = resolveMenuAction(leaf);
        const isParent = a.kind === 'parent';
        // Only real features can be favourited; a group of sub-options is a
        // folder, not a thing to pin.
        const canFav = !isParent;
        const fav = favIds.includes(leaf.id);
        return (
          <MenuItemRow
            key={leaf.id}
            leaf={leaf}
            isParent={isParent}
            canFav={canFav}
            fav={fav}
            onPick={() => onPick(leaf)}
            onToggleFav={() => {
              haptic('select');
              toggleFavourite(leaf.id);
            }}
            onAddFav={() => {
              haptic('select');
              addFavourite(leaf.id);
              showToast(`${leaf.label} added to favourites`);
            }}
          />
        );
      })}
    </>
  );
}

/**
 * A single menu row. A short tap opens the feature; a press-and-hold adds it to
 * the favourites strip, mirroring the overlay-row long-press pattern: the hold
 * fires the add and swallows the tap that would otherwise follow, so we don't
 * also open the feature. Folders and planned stubs aren't holdable, they just tap
 * through. The star stays as an explicit pin/unpin.
 */
function MenuItemRow({
  leaf,
  isParent,
  canFav,
  fav,
  onPick,
  onToggleFav,
  onAddFav,
}: {
  leaf: MenuLeaf;
  isParent: boolean;
  canFav: boolean;
  fav: boolean;
  onPick: () => void;
  onToggleFav: () => void;
  onAddFav: () => void;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longFired = useRef(false);
  const holdable = canFav && !leaf.planned;
  const startPress = () => {
    if (!holdable) return;
    longFired.current = false;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      longFired.current = true;
      onAddFav();
    }, 450);
  };
  const cancelPress = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  return (
    <div className={`acp-menu-row-outer${fav ? ' fav' : ''}`}>
      <button
        type="button"
        className={`acp-menu-row${leaf.planned ? ' planned' : ''}`}
        title={holdable ? 'Long-press to add to favourites' : undefined}
        onPointerDown={holdable ? startPress : undefined}
        onPointerUp={holdable ? cancelPress : undefined}
        onPointerLeave={holdable ? cancelPress : undefined}
        onPointerCancel={holdable ? cancelPress : undefined}
        onClick={() => {
          // A long-press just fired on this row; swallow the click it would
          // otherwise turn into so we don't also open the feature.
          if (longFired.current) {
            longFired.current = false;
            return;
          }
          onPick();
        }}
      >
        {leaf.icon && (
          <span className="ic" aria-hidden="true">
            {leaf.icon}
          </span>
        )}
        <span className="lbl">
          {leaf.label}
          {leaf.subtitle && <span className="sub">{leaf.subtitle}</span>}
        </span>
        {isParent ? (
          <ChevronIcon dir="right" />
        ) : leaf.planned ? (
          <span className="acp-soon">soon</span>
        ) : null}
      </button>
      {canFav && (
        <button
          type="button"
          className={`acp-star${fav ? ' on' : ''}`}
          aria-pressed={fav}
          aria-label={fav ? `Unpin ${leaf.label} from favourites` : `Pin ${leaf.label} to favourites`}
          onClick={onToggleFav}
        >
          {fav ? '★' : '☆'}
        </button>
      )}
    </div>
  );
}

function SearchResults({ query, onPick }: { query: string; onPick: (h: FlatMenuHit) => void }) {
  const hits = searchMenu(query);
  if (!hits.length) return <p className="acp-menu-note">No matches for “{query}”.</p>;
  return (
    <>
      {hits.map((h) => (
        <button key={h.leaf.id} type="button" className="acp-menu-row" onClick={() => onPick(h)}>
          {h.leaf.icon && (
            <span className="ic" aria-hidden="true">
              {h.leaf.icon}
            </span>
          )}
          <span className="lbl">
            {h.leaf.label}
            <span className="sub">{h.trail.join(' › ')}</span>
          </span>
          <ChevronIcon dir="right" />
        </button>
      ))}
    </>
  );
}

/** Run a non-sheet, non-setter command against the real stores or an honest toast. */
function runCommand(
  a: Extract<ResolvedMenuAction, { kind: 'command' }>,
  ctx: {
    openSheet: (s: SheetName) => void;
    setActiveTool: (t: ActiveTool) => void;
    showToast: (m: string) => void;
  },
): SheetName | null {
  // Some commands hand over to a whole sheet; open it up front so the caller can
  // see a sheet was opened and hand over to it rather than close the menu on top.
  const opened = commandOpensSheet(a.command);
  if (opened) ctx.openSheet(opened);
  switch (a.command) {
    case 'measure':
      ctx.setActiveTool('measure');
      ctx.showToast('Measure tool armed — tap two points');
      break;
    case 'passage_wizard':
      // Start a fresh guided passage from scratch, then open the Plotting sheet
      // where the step-by-step conductor runs (no need for a pre-plotted route).
      useRoutePlanStore.getState().reset();
      usePassagePlanStore.getState().start();
      ctx.showToast('Passage plan — let\'s plan it step by step');
      break;
    case 'weather_overlay':
      // Puts the overlay up with its panel open, which is where anyone starts:
      // choose the model and what to draw, then minimise to the time bar. No
      // sheet to open, so the menu just closes behind it.
      useWeatherOverlayStore.getState().open();
      break;
    case 'ebl_vrm':
      ctx.setActiveTool('vrm');
      ctx.showToast('EBL/VRM armed');
      break;
    case 'range_rings':
      ctx.showToast('Range rings — coming soon');
      break;
    case 'set_destination':
      ctx.showToast('Tap the chart, or long-press open water, to set a destination');
      break;
    case 'anchor_watch':
      // The Alarms sheet is opened up front (commandOpensSheet); nothing more.
      break;
    case 'mob': {
      haptic('mob');
      // A distinct rising "got it" tone alongside the buzz, so the crew know the
      // mark registered even in the chaos of a real MOB.
      playMobConfirmation();
      // Shared drop path (also used by the always-on HUD MOB button): drops a
      // real mark at the vessel into the safety store, which the safety layer
      // renders and re-adds through a context loss / self-heal.
      if (dropMobAtVessel()) {
        ctx.showToast('MOB — mark dropped at the vessel');
      } else {
        ctx.showToast('MOB — no vessel position yet');
      }
      break;
    }
    case 'distress':
      ctx.showToast('Distress info — coming soon');
      break;
    case 'provider':
      ctx.showToast('Choose the chart under Layers \u2192 Chart provider');
      break;
  }
  return opened;
}
